/**
 * Shared experiment measurement emission (ADR-071 Rules 13–16).
 *
 * One experiment-agnostic way to make an assignment measurable: emit
 * `experiment_assigned` idempotently (a deterministic insert id lets the
 * vendor collapse re-emissions of the same assignment) and, only when the
 * assigned target is actually visible to the member, `experiment_exposed`
 * carrying that target's anchor fields. Events flow through the canonical
 * product-analytics builders and `captureProductEvent` — the one sanctioned
 * validated funnel (ADR-081) — so the vocabulary guard and capture policy
 * apply to every emission. Best-effort by contract: a missing provider, a
 * validation rejection, or a capture failure never throws and never fails the
 * caller's response; the readout's exposure-compliance guard is the designed
 * detection plane for missing measurement.
 *
 * Binding and draining are shared too: `withBoundAnalyticsSubject` owns the
 * setUser/clearUser envelope (an unbound provider attributes each event to a
 * fresh anonymous distinct id, which breaks insert-id dedupe and person-joined
 * readouts, so a failed bind suppresses emission entirely), and
 * `drainExperimentAnalytics` owns the bounded flush-before-return so a slow or
 * rejected vendor flush never fails or meaningfully delays the caller's
 * response.
 */

import {
  getProvider,
  type AnalyticsProvider,
  type FeatureFlagSnapshot,
} from '@galapagos/observability';
import {
  analyticsSubjectIdSchema,
  buildExperimentAssignedEvent,
  buildExperimentExposedEvent,
  captureProductEvent,
  experimentExposureTargetScope,
} from '@galapagos/product-analytics';
import type { ExperimentAssignment, ExperimentTarget } from './schema';

/**
 * The deterministic assignment insert id — the vendor-side dedupe key that
 * makes per-request re-emission idempotent. One recipe for every emitter.
 */
export function assignmentInsertId(assignment: { assignment_id: string }): string {
  return `expassign:${assignment.assignment_id}`;
}

/**
 * Derives the `is_qa` measurement-provenance value from a flag snapshot the
 * caller already holds: `true` only when the named internal-membership flag
 * evaluated to boolean `true` from a server-sourced, non-default evaluation.
 * Every other shape — `false`, a variant string, a missing key, a
 * default-substituted evaluation, a provider outage — yields `false`.
 *
 * Fail-open is deliberate: `is_qa: false` means the member COUNTS in
 * measurement, which is exactly today's behaviour, so a vendor outage can
 * never silently shrink an experiment's sample. The asymmetric guard (strict
 * provenance required only to EXCLUDE a member) is the mirror image of the
 * enrollment guard, which requires strict provenance to INCLUDE one.
 */
export function qaProvenanceFromSnapshot(
  snapshot: FeatureFlagSnapshot,
  internalMembershipFlagKey: string,
): boolean {
  const evaluation = snapshot.evaluations[internalMembershipFlagKey];
  return (
    evaluation !== undefined &&
    evaluation.value === true &&
    evaluation.source === 'server' &&
    evaluation.usedDefault === false
  );
}

/**
 * The one bounded drain budget: long enough to hand the buffered capture to
 * the vendor client before a Lambda freeze, short enough that a hung vendor
 * flush cannot meaningfully delay the caller's response.
 */
export const EXPERIMENT_ANALYTICS_FLUSH_BUDGET_MS = 250;

/**
 * Best-effort bounded drain of the analytics capture buffer. Races the
 * provider's flush against the budget, always clears the timer, swallows a
 * rejected flush, and no-ops on a provider without `flush`. Never throws.
 */
export async function drainExperimentAnalytics(
  analytics: AnalyticsProvider,
  budgetMs: number = EXPERIMENT_ANALYTICS_FLUSH_BUDGET_MS,
): Promise<void> {
  if (!analytics.flush) return;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const flush = Promise.resolve(analytics.flush());
    const budget = new Promise<void>((resolve) => {
      timeout = setTimeout(resolve, budgetMs);
    });
    await Promise.race([flush, budget]);
  } catch {
    // Best-effort drain: a rejected flush must not fail the caller's response.
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

/**
 * The subject-bound emission envelope: validates the identity as a canonical
 * analytics subject, binds it, runs the caller's emission, and unbinds in
 * `finally`. Suppresses emission entirely — returning `false` — when the
 * identity is not a canonical subject or the bind fails, because an unbound
 * capture would attribute the events to a fresh anonymous distinct id,
 * breaking insert-id dedupe and person-joined readouts. A throwing emission is
 * swallowed after the unbind (emission is best-effort by contract), but still
 * counts as attempted so callers gate their terminal drain correctly. Never
 * throws.
 */
export function withBoundAnalyticsSubject(
  analytics: AnalyticsProvider,
  identity: string,
  emit: () => void,
): boolean {
  const subject = analyticsSubjectIdSchema.safeParse(identity);
  if (!subject.success) return false;
  try {
    analytics.setUser(subject.data);
  } catch {
    // A failed bind must suppress emission entirely — nothing anonymous.
    // Clear defensively in case the throwing bind left partial person state
    // on the process-wide singleton.
    try {
      analytics.clearUser();
    } catch {
      // Identity isolation is best effort and must not fail the caller.
    }
    return false;
  }
  try {
    emit();
  } catch {
    // Emission is strictly best-effort; a throwing emitter must not fail the
    // caller. The finally below still unbinds.
  } finally {
    // Unbind even when emission throws: the provider is a process-wide
    // singleton, and a leaked binding would misattribute later unbound
    // captures on this warm container to this member.
    try {
      analytics.clearUser();
    } catch {
      // Identity isolation is best effort and must not fail the caller.
    }
  }
  return true;
}

/**
 * ADR-071 Rule 16: the vendor attribution property for events OUTSIDE the
 * `experiment_*` family (outcome/domain events emitted while the host holds an
 * assignment). The measurement emission below attaches it to its own events.
 */
export function featureAttribution(assignment: {
  flag_key: string;
  variant_key: string;
}): Record<string, string> {
  return { [`$feature/${assignment.flag_key}`]: assignment.variant_key };
}

type Scalar = string | number | boolean;

function attribution(assignment: ExperimentAssignment) {
  return {
    experiment_key: assignment.experiment_key,
    experiment_version: assignment.experiment_version,
    flag_key: assignment.flag_key,
    variant_key: assignment.variant_key,
    assignment_id: assignment.assignment_id,
    [`$feature/${assignment.flag_key}`]: assignment.variant_key,
  };
}

/**
 * Scope-driven anchor mapping: every ADR-071 target scope carries its anchor
 * fields onto the exposure event, with the kebab `target_scope` join key owned
 * by the shared converter. A future scope ships anchored without changes here
 * beyond its case arm.
 */
function targetAnchors(target: ExperimentTarget) {
  const scoped = {
    target_scope: experimentExposureTargetScope(target.scope),
  };
  switch (target.scope) {
    case 'agent_definition':
      return {
        ...scoped,
        agent_definition_id: target.agent_definition_id,
        agent_definition_version: target.agent_definition_version,
      };
    case 'agent_screen_element':
      return {
        ...scoped,
        agent_definition_id: target.agent_definition_id,
        agent_definition_version: target.agent_definition_version,
        agent_screen_id: target.agent_screen_id,
        agent_screen_version: target.agent_screen_version,
        agent_screen_element_id: target.agent_screen_element_id,
      };
    case 'first_message':
      return {
        ...scoped,
        agent_definition_id: target.agent_definition_id,
        agent_definition_version: target.agent_definition_version,
        first_message_pool: target.first_message_pool,
      };
    case 'genui_flow':
      return { ...scoped, flow_id: target.flow_id, flow_version: target.flow_version };
    case 'genui_step':
      return {
        ...scoped,
        flow_id: target.flow_id,
        flow_version: target.flow_version,
        step_key: target.step_key,
        screen_id: target.screen_id,
      };
    case 'genui_element':
      return {
        ...scoped,
        flow_id: target.flow_id,
        flow_version: target.flow_version,
        step_key: target.step_key,
        screen_id: target.screen_id,
        element_key: target.element_key,
      };
    case 'genui_surface':
      return {
        ...scoped,
        surface_id: target.surface_id,
        schema_version: target.schema_version,
        screen_id: target.screen_id,
      };
    case 'genui_surface_element':
      return {
        ...scoped,
        surface_id: target.surface_id,
        schema_version: target.schema_version,
        screen_id: target.screen_id,
        element_key: target.element_key,
      };
    default: {
      // Compile-time exhaustiveness: a new target scope must add its anchor
      // arm here or this assignment fails to typecheck.
      const exhaustive: never = target;
      return exhaustive;
    }
  }
}

// The only string reasons the warn log emits verbatim — a closed set so no
// caller (present or future) can route dynamic text into the log.
const STATIC_FAILURE_REASONS = new Set(['missing_target']);

function warnEmissionFailure(experimentKey: string, event: string, cause: unknown): void {
  try {
    getProvider().logs.log('warn', 'galapagos.experiments.measurement_emission_failed', {
      experiment_key: experimentKey,
      event_kind: event,
      // Bounded, member-safe failure class (an error name or a static reason
      // string) so a validation rejection is distinguishable from a transport
      // throw without local reproduction.
      reason_class: failureReasonClass(cause),
    });
  } catch {
    // Observability failures never affect the caller.
  }
}

function failureReasonClass(cause: unknown): string {
  if (typeof cause === 'string') return STATIC_FAILURE_REASONS.has(cause) ? cause : 'unknown';
  return cause instanceof Error && cause.name ? cause.name : 'unknown';
}

export interface ExperimentMeasurementInput {
  assignment: ExperimentAssignment;
  /**
   * The target whose visibility drives exposure; its anchors ride the event.
   * Omit when the caller found no target for its own surface — the assignment
   * still emits (it needs no anchors), exposure cannot, and a visible-but-
   * unanchored call is warn-visible rather than silently unmeasured.
   */
  target?: ExperimentTarget;
  /** Whether the target is actually visible to the member on this response. */
  visible: boolean;
  /** Server-stamped member-local timestamp (offset-bearing ISO), when a zone is known. */
  memberLocalTimestamp?: string;
  /** Capture instant for `preserveTimestamp` semantics; omit for vendor-now. */
  now?: Date;
  /** Extra assigned-event properties (e.g. `used_default`, `is_qa`). */
  assignedExtra?: Record<string, Scalar>;
  /** Extra exposed-event properties. */
  exposedExtra?: Record<string, Scalar>;
}

/**
 * What `emitExperimentMeasurement` actually handed to the capture buffer.
 * `assigned` is the enrolment fence: a caller counting successful emissions
 * must gate on it, because a throwing capture is swallowed here (warn-only)
 * and is otherwise invisible at the call site.
 */
export interface ExperimentMeasurementResult {
  assigned: boolean;
  exposed: boolean;
}

/**
 * Emit assignment (always, idempotent) and exposure (when visible, anchored).
 * Never throws; the returned result reports which captures were accepted.
 */
export function emitExperimentMeasurement(
  analytics: AnalyticsProvider | undefined,
  input: ExperimentMeasurementInput,
): ExperimentMeasurementResult {
  const result: ExperimentMeasurementResult = { assigned: false, exposed: false };
  if (!analytics) return result;
  const base = attribution(input.assignment);
  const timestampProps: Record<string, string> = input.memberLocalTimestamp
    ? { member_local_timestamp: input.memberLocalTimestamp }
    : {};
  const captureOptions: { timestamp?: string; preserveTimestamp?: boolean } = input.now
    ? { timestamp: input.now.toISOString(), preserveTimestamp: true }
    : {};

  // Per-event envelopes: a rejected exposure never suppresses the assignment
  // (and vice versa), and every suppression is warn-visible without member data.
  try {
    captureProductEvent(
      analytics,
      buildExperimentAssignedEvent(
        {
          ...base,
          assignment_source: 'server',
          ...timestampProps,
          ...(input.assignedExtra ?? {}),
        },
        { ...captureOptions, insertId: assignmentInsertId(input.assignment) },
      ),
    );
    result.assigned = true;
  } catch (cause) {
    warnEmissionFailure(input.assignment.experiment_key, 'assigned', cause);
  }

  if (!input.visible) return result;
  if (!input.target) {
    // A variant carried to the member with no target for the emitting surface
    // cannot anchor an exposure — the Rules 13/14 gap this module closes must
    // never regress silently.
    warnEmissionFailure(input.assignment.experiment_key, 'exposed', 'missing_target');
    return result;
  }
  try {
    captureProductEvent(
      analytics,
      buildExperimentExposedEvent(
        {
          ...base,
          ...targetAnchors(input.target),
          ...timestampProps,
          ...(input.exposedExtra ?? {}),
        },
        captureOptions,
      ),
    );
    result.exposed = true;
  } catch (cause) {
    warnEmissionFailure(input.assignment.experiment_key, 'exposed', cause);
  }
  return result;
}
